/* istanbul ignore file */

import type { Client } from 'urql';

import type {
  MapBounds,
  PositionCoordinates,
} from '../../LocationSearch.types';
import type { LocationGroups } from '../../LocationSearch.types';
import {
  locationsSearchByAreaQuery,
  locationsSearchByStringQuery,
} from '../../queries';
import {
  checkIfNonNullable,
  extractLocation,
  formatAndSplitLocations,
} from './locations.helpers';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fetches locations based on a search string
 */
export async function searchLocationsByString(
  client: Client,
  context: SearchLocationsByStringContext,
): Promise<LocationGroups> {
  const { currentPosition, searchString } = context;

  if (!searchString) return { pickup: [], outpost: [] };

  const { data } = await locationsSearchByStringQuery(client, { searchString });
  const storeLocations = data?.searchLocationsByString;

  if (!storeLocations) {
    throw new Error('Failed to fetch locations by string');
  }

  const locations = storeLocations
    .filter(checkIfNonNullable)
    .map(extractLocation);

  return formatAndSplitLocations(locations, currentPosition);
}

/**
 * Fetches locations based on a search area
 */
export async function searchLocationsByBoundingBox(
  client: Client,
  context: SearchLocationsByBoundingBoxContext,
): Promise<LocationGroups> {
  const { currentPosition, searchArea } = context;

  if (!searchArea) return { pickup: [], outpost: [] };

  const { data } = await locationsSearchByAreaQuery(client, searchArea);
  const storeLocations = data?.searchLocationsByBoundingBox;

  if (!storeLocations) {
    throw new Error('Failed to fetch locations by bound box');
  }

  const locations = storeLocations
    .filter(checkIfNonNullable)
    .map(extractLocation);

  return formatAndSplitLocations(locations, currentPosition);
}

// ─── Types ───────────────────────────────────────────────────────────────────

type SearchLocationsByStringContext = {
  readonly searchString: string;
  readonly currentPosition: PositionCoordinates | undefined;
};

type SearchLocationsByBoundingBoxContext = {
  readonly searchArea: MapBounds | undefined;
  readonly currentPosition: PositionCoordinates | undefined;
};
